import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Loader2, ArrowLeft, Workflow, ArrowRight, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { useBranch } from '../contexts/BranchContext';
import { endgoodApiService, Endgood } from '../services/endgoodApi';
import { bomHeaderApiService, BomHeader } from '../services/bomHeaderApi';
import { bomDetailApiService, BomDetail } from '../services/bomDetailApi';
import { processFlowApiService, ProcessFlow } from '../services/processFlowApi';
import {
  finishedGoodsInventoryApiService,
  FinishedGoodsInventoryExtendedSummary,
  FinishedGoodsInventoryAgingDetail
} from '../services/finishedGoodsInventoryApi';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

const AGE_BUCKET_ORDER = ['0-30', '31-60', '61-90', '91-120', '>120', 'Unknown'];

interface RawMaterialView {
  id: number;
  processName: string;
  categoryOfItem: string;
  item: string;
  quantity: number;
  unit: string;
  priority?: number;
}

const EndgoodViewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { selectedBranch } = useBranch();

  const [endgood, setEndgood] = useState<Endgood | null>(null);
  const [endgoodLoading, setEndgoodLoading] = useState(false);

  const [bomHeaders, setBomHeaders] = useState<BomHeader[]>([]);
  const [bomLoading, setBomLoading] = useState(false);
  const [bomDetails, setBomDetails] = useState<Record<number, BomDetail[]>>({});
  const [bomDetailsLoading, setBomDetailsLoading] = useState(false);
  const [routingByBom, setRoutingByBom] = useState<Record<number, ProcessFlow | null>>({});
  const [routingLoadingByBom, setRoutingLoadingByBom] = useState<Record<number, boolean>>({});

  const [inventoryRows, setInventoryRows] = useState<FinishedGoodsInventoryExtendedSummary[]>([]);
  const [inventoryLoading, setInventoryLoading] = useState(false);

  const [agingDetails, setAgingDetails] = useState<FinishedGoodsInventoryAgingDetail[]>([]);
  const [agingLoading, setAgingLoading] = useState(false);

  useEffect(() => {
    if (!id) return;
    loadEndgood(id);
  }, [id]);

  useEffect(() => {
    if (!id) return;
    loadBomHeaders(id);
  }, [id]);

  useEffect(() => {
    if (!id) return;
    const branchId = selectedBranch || undefined;
    loadInventory(id, branchId);
    loadAgingDetails(branchId, id);
  }, [id, selectedBranch, endgood?.endgoodName]);

  const loadEndgood = async (endgoodId: string) => {
    try {
      setEndgoodLoading(true);
      const data = await endgoodApiService.getEndgoodById(endgoodId);
      setEndgood(data);
    } catch (error) {
      toast.error('Failed to load endgood details');
    } finally {
      setEndgoodLoading(false);
    }
  };

  const loadBomHeaders = async (endgoodId: string) => {
    try {
      setBomLoading(true);
      const data = await bomHeaderApiService.getBomHeadersByEndgood(Number(endgoodId));
      setBomHeaders(data);
      loadBomDetailsAndRouting(data);
    } catch (error) {
      setBomHeaders([]);
    } finally {
      setBomLoading(false);
    }
  };

  const loadBomDetailsAndRouting = async (headers: BomHeader[]) => {
    if (headers.length === 0) {
      setBomDetails({});
      setRoutingByBom({});
      return;
    }

    try {
      setBomDetailsLoading(true);
      const detailPromises = headers.map(async (header) => {
        const details = await bomDetailApiService.getBomDetailsByHeader(header.id);
        return { headerId: header.id, details };
      });
      const detailResults = await Promise.all(detailPromises);
      const detailMap: Record<number, BomDetail[]> = {};
      detailResults.forEach((result) => {
        detailMap[result.headerId] = result.details;
      });
      setBomDetails(detailMap);
    } catch (error) {
      setBomDetails({});
    } finally {
      setBomDetailsLoading(false);
    }

    headers.forEach(async (header) => {
      if (!header.processFlow?.id) {
        setRoutingByBom(prev => ({ ...prev, [header.id]: null }));
        return;
      }
      setRoutingLoadingByBom(prev => ({ ...prev, [header.id]: true }));
      try {
        const routingData = await processFlowApiService.getProcessFlowById(header.processFlow.id.toString());
        const routingSteps = await processFlowApiService.getProcessStepsByProcessFlow(routingData.id);
        setRoutingByBom(prev => ({
          ...prev,
          [header.id]: { ...routingData, processSteps: routingSteps },
        }));
      } catch (error) {
        setRoutingByBom(prev => ({ ...prev, [header.id]: null }));
      } finally {
        setRoutingLoadingByBom(prev => ({ ...prev, [header.id]: false }));
      }
    });
  };

  const loadInventory = async (endgoodId: string, branchId?: number) => {
    try {
      setInventoryLoading(true);
      const response = await finishedGoodsInventoryApiService.getFinishedGoodsInventoryExtendedView(
        branchId,
        undefined,
        Number(endgoodId)
      );
      if (response.success && response.data) {
        const filtered = response.data.filter(row => {
          if (row.endgoodId !== undefined) {
            return String(row.endgoodId) === endgoodId;
          }
          return row.endgoodName === endgood?.endgoodName;
        });
        setInventoryRows(filtered);
      } else {
        setInventoryRows([]);
      }
    } catch (error) {
      setInventoryRows([]);
    } finally {
      setInventoryLoading(false);
    }
  };

  const loadAgingDetails = async (branchId?: number, endgoodId?: string) => {
    try {
      setAgingLoading(true);
      const response = await finishedGoodsInventoryApiService.getInventoryAgingDetails(
        branchId,
        undefined,
        undefined,
        undefined,
        endgoodId ? Number(endgoodId) : undefined
      );
      if (response.success && response.data) {
        setAgingDetails(response.data);
      } else {
        setAgingDetails([]);
      }
    } catch (error) {
      setAgingDetails([]);
    } finally {
      setAgingLoading(false);
    }
  };

  const normalizedEndgoodId = useMemo(() => (id ? Number(id) : null), [id]);

  const endgoodInventory = useMemo(() => {
    if (!normalizedEndgoodId) return null;
    return inventoryRows.find(row => row.endgoodId === normalizedEndgoodId) || null;
  }, [inventoryRows, normalizedEndgoodId]);

  const filteredAgingDetails = useMemo(() => {
    if (!endgood) return [];
    return agingDetails.filter(row => {
      if (row.endgoodId) {
        return row.endgoodId === endgood.id;
      }
      return row.endgoodName === endgood.endgoodName;
    });
  }, [agingDetails, endgood]);

  const endgoodAgingChartData = useMemo(() => {
    const normalizeBucket = (row: FinishedGoodsInventoryAgingDetail): string => {
      return (
        row.bucketLabel ||
        row.agingBucket ||
        row.ageBucket ||
        row.bucket ||
        row.label ||
        'Unknown'
      );
    };

    const grouped = filteredAgingDetails.reduce<Record<string, { packedQty: number; wipQty: number }>>(
      (acc, row) => {
        const bucket = normalizeBucket(row);
        const qty = row.totalQuantity ?? row.quantity ?? 0;
        if (!acc[bucket]) {
          acc[bucket] = { packedQty: 0, wipQty: 0 };
        }
        if (row.inventoryType === 'PACKED') {
          acc[bucket].packedQty += qty;
        } else if (row.inventoryType === 'WIP') {
          acc[bucket].wipQty += qty;
        }
        return acc;
      },
      {}
    );

    return Object.entries(grouped)
      .map(([bucket, totals]) => ({
        bucket,
        ...totals,
        totalQty: totals.packedQty + totals.wipQty,
      }))
      .filter(item => item.totalQty > 0)
      .sort((a, b) => {
        const aIndex = AGE_BUCKET_ORDER.indexOf(a.bucket);
        const bIndex = AGE_BUCKET_ORDER.indexOf(b.bucket);
        if (aIndex === -1 && bIndex === -1) return 0;
        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;
        return aIndex - bIndex;
      });
  }, [filteredAgingDetails]);

  const wipDetails = useMemo(() => {
    return filteredAgingDetails
      .filter(row => row.inventoryType === 'WIP')
      .sort((a, b) => (b.totalQuantity ?? b.quantity ?? 0) - (a.totalQuantity ?? a.quantity ?? 0));
  }, [filteredAgingDetails]);

  const packedDetails = useMemo(() => {
    return filteredAgingDetails
      .filter(row => row.inventoryType === 'PACKED')
      .sort((a, b) => (b.totalQuantity ?? b.quantity ?? 0) - (a.totalQuantity ?? a.quantity ?? 0));
  }, [filteredAgingDetails]);

  const formatNumber = (value: number | null | undefined): string => {
    if (value === null || value === undefined || isNaN(value)) return '0';
    return value.toLocaleString('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  const convertBomDetails = (details: BomDetail[]): RawMaterialView[] => {
    const materials = details.map((detail) => {
      const processName = detail.process?.processName || 'N/A';
      const categoryName =
        detail.rawCategory?.categoryName ||
        detail.rawItem?.itemCategory?.categoryName ||
        'Raw Material';
      const itemName =
        detail.rawItem?.itemName ||
        detail.endgood?.endgoodName ||
        (detail.endgoodId ? `Endgood #${detail.endgoodId}` : 'N/A');
      let unit = detail.unit || 'kg';
      if (detail.endgoodId) {
        unit = detail.unit || 'pcs';
      } else if (detail.rawItem?.itemUnit) {
        unit = detail.unit || detail.rawItem.itemUnit;
      }

      return {
        id: detail.id,
        processName,
        categoryOfItem: categoryName,
        item: itemName,
        quantity: detail.quantity || 0,
        unit,
        priority: detail.priority,
      };
    });

    return [...materials].sort((a, b) => {
      const processCompare = (a.processName || '').localeCompare(b.processName || '');
      if (processCompare !== 0) return processCompare;
      return (a.priority ?? 0) - (b.priority ?? 0);
    });
  };

  const ProcessFlowDiagram = ({ processFlow }: { processFlow: ProcessFlow }) => {
    if (!processFlow.processSteps || processFlow.processSteps.length === 0) {
      return (
        <div className="p-4 bg-gradient-to-r from-primary-50/30 to-primary-100/30 rounded-lg border border-primary-200">
          <div className="flex items-center gap-2 mb-4">
            <Workflow className="h-5 w-5 text-primary-600" />
            <h4 className="font-medium text-primary-800">Process Flow</h4>
          </div>
          <div className="flex items-center justify-center p-8">
            <span className="text-primary-600">No process steps found for this routing</span>
          </div>
        </div>
      );
    }

    return (
      <div className="p-4 bg-gradient-to-r from-primary-50/30 to-primary-100/30 rounded-lg border border-primary-200">
        <div className="flex items-center gap-2 mb-4">
          <Workflow className="h-5 w-5 text-primary-600" />
          <h4 className="font-medium text-primary-800">Process Flow</h4>
        </div>
        <div className="flex items-center gap-2 overflow-x-auto pb-2">
          {processFlow.processSteps
            .sort((a, b) => a.sequenceOrder - b.sequenceOrder)
            .filter((processStep, index, array) =>
              array.findIndex(step => step.id === processStep.id) === index
            )
            .map((processStep, index) => (
              <React.Fragment key={processStep.id}>
                <div className="flex-shrink-0">
                  <div className="bg-white border-2 border-primary-300 rounded-lg p-3 min-w-[120px] text-center">
                    <div className="text-sm font-medium text-primary-800">{processStep.process.processName}</div>
                    <div className="text-xs text-gray-600 mt-1">Seq: {processStep.sequenceOrder}</div>
                    {processStep.cycleTime > 0 && (
                      <div className="text-xs text-primary-600 mt-1">{processStep.cycleTime} min</div>
                    )}
                  </div>
                </div>
                {index < processFlow.processSteps.length - 1 && (
                  <ArrowRight className="h-5 w-5 text-primary-500 flex-shrink-0" />
                )}
              </React.Fragment>
            ))}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100/50 p-6">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={() => navigate('/endgoods')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Endgoods
          </Button>
        </div>

        <Card className="border-primary-200 bg-white shadow-lg">
          <CardHeader>
            <CardTitle className="text-xl font-semibold text-gray-800">Endgood Details</CardTitle>
          </CardHeader>
          <CardContent>
            {endgoodLoading ? (
              <div className="flex items-center justify-center h-28 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                Loading endgood details...
              </div>
            ) : endgood ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <div className="text-sm text-muted-foreground">Endgood Name</div>
                  <div className="font-semibold">{endgood.endgoodName}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Part Number</div>
                  <div className="font-semibold">{endgood.partNumber || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">SKU</div>
                  <div className="font-semibold">{endgood.sku || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Category</div>
                  <div className="font-semibold">{endgood.endgoodCategory?.categoryName || 'Uncategorized'}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">HSN</div>
                  <div className="font-semibold">{endgood.hsn?.hsnCode || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Production Weight (g)</div>
                  <div className="font-semibold">{formatNumber(endgood.productionWeight)}</div>
                </div>
                <div className="md:col-span-2 lg:col-span-3">
                  <div className="text-sm text-muted-foreground">Description</div>
                  <div className="font-semibold">{endgood.description || 'N/A'}</div>
                </div>
              </div>
            ) : (
              <div className="text-muted-foreground">No endgood data available.</div>
            )}
          </CardContent>
        </Card>

        <Card className="border-primary-200 bg-white shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-gray-800">Endgood BOM & Routing</CardTitle>
          </CardHeader>
          <CardContent>
            {bomLoading ? (
              <div className="flex items-center justify-center h-28 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                Loading BOM details...
              </div>
            ) : bomHeaders.length > 0 ? (
              <div className="space-y-6">
                {bomHeaders.map((bom) => {
                  const details = bomDetails[bom.id] || [];
                  const materials = convertBomDetails(details);
                  const routing = routingByBom[bom.id];
                  const routingLoading = routingLoadingByBom[bom.id];

                  return (
                    <div key={bom.id} className="border border-primary-200 rounded-lg overflow-hidden">
                      <div className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-b border-primary-200 p-4">
                        <div className="flex flex-wrap gap-4 text-sm text-gray-700">
                          <div>
                            <div className="text-xs text-gray-500">BOM Number</div>
                            <div className="font-semibold">{bom.bomNumber}</div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-500">BOM Name</div>
                            <div className="font-semibold">{bom.bomName}</div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-500">Version</div>
                            <div className="font-semibold">{bom.version}</div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-500">Routing</div>
                            <div className="font-semibold">{bom.processFlow?.name || 'N/A'}</div>
                          </div>
                        </div>
                      </div>

                      <div className="p-4 space-y-6">
                        <div>
                          {routingLoading ? (
                            <div className="p-4 bg-gradient-to-r from-primary-50/30 to-primary-100/30 rounded-lg border border-primary-200">
                              <div className="flex items-center gap-2 mb-4">
                                <Workflow className="h-5 w-5 text-primary-600" />
                                <h4 className="font-medium text-primary-800">Process Flow</h4>
                              </div>
                              <div className="flex items-center justify-center p-8">
                                <div className="flex items-center gap-2">
                                  <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
                                  <span className="text-primary-600">Loading routing details...</span>
                                </div>
                              </div>
                            </div>
                          ) : routing ? (
                            <ProcessFlowDiagram processFlow={routing} />
                          ) : (
                            <div className="p-4 bg-gradient-to-r from-primary-50/30 to-primary-100/30 rounded-lg border border-primary-200">
                              <div className="flex items-center gap-2 mb-4">
                                <Workflow className="h-5 w-5 text-primary-600" />
                                <h4 className="font-medium text-primary-800">Process Flow</h4>
                              </div>
                              <div className="flex items-center justify-center p-8">
                                <span className="text-primary-600">No routing selected</span>
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="border border-primary-200 rounded-lg overflow-hidden">
                          <div className="bg-gradient-to-r from-primary-50 to-primary-100/50 border-b border-primary-200 px-4 py-3">
                            <div className="flex items-center gap-2 text-primary-800 font-semibold">
                              <FileText className="h-5 w-5 text-primary-700" />
                              Raw Materials Used
                            </div>
                          </div>
                          <div className="p-4">
                            {bomDetailsLoading ? (
                              <div className="flex items-center justify-center h-28 text-muted-foreground">
                                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                                Loading BOM details...
                              </div>
                            ) : materials.length > 0 ? (
                              <div className="border border-primary-200 rounded-lg overflow-hidden">
                                <Table>
                                  <TableHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                                    <TableRow className="border-primary-200">
                                      <TableHead>Process Name</TableHead>
                                      <TableHead>Category of Item</TableHead>
                                      <TableHead className="w-64">Item</TableHead>
                                      <TableHead className="w-40">Quantity</TableHead>
                                      <TableHead className="w-20">Unit</TableHead>
                                      <TableHead className="w-24">Priority</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {materials.map((material) => (
                                      <TableRow key={material.id} className="border-primary-100">
                                        <TableCell>
                                          <span className="font-medium">{material.processName}</span>
                                        </TableCell>
                                        <TableCell>{material.categoryOfItem}</TableCell>
                                        <TableCell>{material.item}</TableCell>
                                        <TableCell>{formatNumber(material.quantity)}</TableCell>
                                        <TableCell>{material.unit}</TableCell>
                                        <TableCell>{material.priority ?? '-'}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </div>
                            ) : (
                              <div className="text-muted-foreground">No BOM details available.</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-muted-foreground">No BOM details available.</div>
            )}
          </CardContent>
        </Card>

        <Card className="border-primary-200 bg-white shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-gray-800">Endgood Inventory</CardTitle>
          </CardHeader>
          <CardContent>
            {inventoryLoading ? (
              <div className="flex items-center justify-center h-28 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                Loading inventory details...
              </div>
            ) : endgoodInventory ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <div className="text-sm text-muted-foreground">WIP Qty</div>
                  <div className="font-semibold">{formatNumber(endgoodInventory.wipQuantity)}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Ready to Pack Qty</div>
                  <div className="font-semibold">{formatNumber(endgoodInventory.readyToPackQuantity)}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Packed Qty</div>
                  <div className="font-semibold">{formatNumber(endgoodInventory.packedQuantity)}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Total Qty</div>
                  <div className="font-semibold">
                    {formatNumber(
                      (endgoodInventory.wipQuantity ?? 0) +
                      (endgoodInventory.readyToPackQuantity ?? 0) +
                      (endgoodInventory.packedQuantity ?? 0)
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-muted-foreground">No inventory data available.</div>
            )}
          </CardContent>
        </Card>

        <Card className="border-primary-200 bg-white shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-gray-800">Endgood Aging</CardTitle>
          </CardHeader>
          <CardContent>
            {agingLoading ? (
              <div className="flex items-center justify-center h-28 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                Loading aging details...
              </div>
            ) : endgoodAgingChartData.length > 0 ? (
              <div className="w-full">
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={endgoodAgingChartData} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="bucket" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip
                      formatter={(value: number, name: string) => [
                        formatNumber(value || 0),
                        name === 'packedQty' ? 'Packed' : 'WIP',
                      ]}
                    />
                    <Bar dataKey="packedQty" radius={[6, 6, 0, 0]} fill="#60A5FA" />
                    <Bar dataKey="wipQty" radius={[6, 6, 0, 0]} fill="#F59E0B" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="text-muted-foreground">No aging data available.</div>
            )}
          </CardContent>
        </Card>

        <Card className="border-primary-200 bg-white shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-gray-800">Endgood WIP</CardTitle>
          </CardHeader>
          <CardContent>
            {agingLoading ? (
              <div className="flex items-center justify-center h-28 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                Loading WIP work orders...
              </div>
            ) : wipDetails.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Work Order No</TableHead>
                    <TableHead>Aging Bucket</TableHead>
                    <TableHead>Quantity</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {wipDetails.map((row, index) => (
                    <TableRow key={`${row.workOrderId ?? row.endgoodId ?? 'wip'}-${index}`}>
                      <TableCell className="font-medium">
                        {row.workOrderId ? (
                          <a
                            href={`/work-orders/${row.workOrderId}/view`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-primary-700 hover:underline"
                          >
                            {row.workOrderNumber || `WO-${row.workOrderId}`}
                          </a>
                        ) : (
                          row.workOrderNumber || 'N/A'
                        )}
                      </TableCell>
                      <TableCell>{row.agingBucket || row.bucketLabel || row.ageBucket || 'N/A'}</TableCell>
                      <TableCell>{formatNumber(row.totalQuantity ?? row.quantity ?? 0)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-muted-foreground">No WIP work orders available.</div>
            )}
          </CardContent>
        </Card>

        <Card className="border-primary-200 bg-white shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-gray-800">Endgood Packed</CardTitle>
          </CardHeader>
          <CardContent>
            {agingLoading ? (
              <div className="flex items-center justify-center h-28 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                Loading packed details...
              </div>
            ) : packedDetails.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Batch Id</TableHead>
                    <TableHead>Aging Bucket</TableHead>
                    <TableHead>Quantity</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {packedDetails.map((row, index) => (
                    <TableRow key={`${row.workOrderId ?? row.endgoodId ?? 'packed'}-${index}`}>
                      <TableCell className="font-medium">
                        {row.batchId ? (
                          <a
                            href={`/packing-entries/batch/${row.batchId}/view`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-primary-700 hover:underline"
                          >
                            {row.batchId}
                          </a>
                        ) : (
                          'N/A'
                        )}
                      </TableCell>
                      <TableCell>{row.agingBucket || row.bucketLabel || row.ageBucket || 'N/A'}</TableCell>
                      <TableCell>{formatNumber(row.totalQuantity ?? row.quantity ?? 0)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-muted-foreground">No packed work orders available.</div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EndgoodViewPage;
